import { type FC, type PropsWithChildren, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useFeatureFlagCached } from "@/domain/_shared/feature-flag/hooks/api/use-feature-flag-cached";
import { loadLocaleBundle } from "@/lib/i18n";

import { resolveLocale } from "./resolve-locale";

interface I18nProviderProps extends PropsWithChildren {
  workspaceId: string;
  userLocale: string | null | undefined;
  workspaceLocale: string | null | undefined;
}

export const I18nProvider: FC<I18nProviderProps> = ({ workspaceId, userLocale, workspaceLocale, children }) => {
  const { i18n } = useTranslation();
  const { isEnabled: isI18nEnabled } = useFeatureFlagCached({
    workspaceId,
    id: "enable-i18n",
  });

  // For admin pages, always enable i18n to respect user locale preference
  const shouldEnableI18n = workspaceId === "admin" || isI18nEnabled;

  useEffect(() => {
    // Always respect user locale if set, regardless of feature flag
    // Feature flag only controls whether workspace locale is used as fallback
    const effectiveWorkspaceLocale = shouldEnableI18n ? workspaceLocale : null;
    const locale = resolveLocale({ userLocale, workspaceLocale: effectiveWorkspaceLocale });

    if (locale === i18n.language) {
      document.documentElement.lang = locale;
      return;
    }

    let isCurrentRequest = true;

    /**
     * WCAG 3.1.1: index.html hardcodes lang="de", so the attribute has to follow the locale or
     * the page misdeclares its language and screen readers apply the wrong pronunciation.
     *
     * It is set together with the language change rather than ahead of it: declaring the new
     * locale while a failed bundle load leaves the UI in the old one is a confident lie, which
     * is worse for a screen reader than the stale value. The guard drops a slow response that
     * a newer locale has already superseded.
     */
    const applyLocale = async (): Promise<void> => {
      if (!isCurrentRequest) {
        return;
      }

      await i18n.changeLanguage(locale);
      if (!isCurrentRequest) {
        return;
      }

      document.documentElement.lang = locale;
    };

    const reportFailure = (error: unknown) => {
      console.error("[i18n] Failed to apply locale", locale, error);
    };

    if (locale === "de") {
      applyLocale().catch(reportFailure);
    } else {
      loadLocaleBundle(locale).then(applyLocale).catch(reportFailure);
    }

    return () => {
      isCurrentRequest = false;
    };
  }, [shouldEnableI18n, userLocale, workspaceLocale, i18n]);

  return <>{children}</>;
};
